// OperationsDashboard.jsx
//
// Main Operations Dashboard page.
// Route (suggested): /dashboard/operations
//
// Data hierarchy rendered: Date -> Summary -> Camp Status -> Clients -> Locations -> Camps
// Clicking a camp navigates to /dashboard/operations/camp/:campId

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOperationsDashboard } from './operationsApi';
import {
  formatDateYMD,
  parseYMD,
  addDays,
  addMonths,
  isSameDay,
  formatDisplayDate,
  formatMonthLabel,
  getOperationsErrorMessage,
  num,
} from './operationHelpers';
import {
  BRAND,
  STATUS_STYLES,
  CAMP_STATUS_BADGE,
  RadialProgress,
  StatusDonut,
  SkeletonBlock,
  IconUsers,
  IconClipboard,
  IconCheckCircle,
  IconClock,
  IconBuilding,
  IconMapPin,
  IconChevronDown,
  IconLogOut,
  IconRefreshCw,
  IconStethoscope,
} from './operationsUi';

function SummaryCards({ summary }) {
  const cards = [
    { label: 'Total Camps', value: num(summary?.total_camps), icon: IconBuilding },
    { label: 'Total Patients', value: num(summary?.total_patients), icon: IconUsers },
    { label: 'Total Services', value: num(summary?.total_services), icon: IconClipboard },
    { label: 'Completed Services', value: num(summary?.completed_services), icon: IconCheckCircle, accent: BRAND.green },
    { label: 'Pending Services', value: num(summary?.pending_services), icon: IconClock, accent: BRAND.amber },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map((c) => {
        const Icon = c.icon;
        const accent = c.accent || BRAND.teal;
        return (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
              style={{ backgroundColor: `${accent}1a`, color: accent }}
            >
              <Icon className="w-4 h-4" />
            </div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{c.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{c.value}</p>
          </div>
        );
      })}

      {/* Overall progress gets the signature radial ring instead of a bare number */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex items-center gap-3">
        <RadialProgress percentage={summary?.progress_percentage} size={48} stroke={5} />
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Overall</p>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Progress</p>
        </div>
      </div>
    </div>
  );
}

function CampStatusSummary({ campStatus }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
      <StatusDonut campStatus={campStatus} />
    </div>
  );
}

function CampCard({ camp, onClick }) {
  const statusKey = (camp?.status || 'SCHEDULED').toLowerCase();
  const badgeClass = CAMP_STATUS_BADGE[camp?.status] || 'bg-gray-100 text-gray-700';
  const stripColor =
    {
      SCHEDULED: '#94a3b8',
      READY: BRAND.teal,
      IN_PROGRESS: BRAND.amber,
      COMPLETED: BRAND.green,
      OVERDUE: BRAND.red,
    }[camp?.status] || '#94a3b8';

  return (
    <button
      onClick={onClick}
      className="relative w-full text-left bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md hover:border-[#99dfdb] transition-all pl-4 pr-4 py-4 overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#11a8a4] focus:ring-offset-1"
    >
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: stripColor }} />

      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="font-semibold text-slate-900">{camp?.branch_name || camp?.location || 'Camp'}</p>
          <p className="text-xs text-slate-500">{camp?.branch_code}</p>
        </div>
        <span className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${badgeClass}`}>
          {statusKey.replace(/_/g, ' ')}
        </span>
      </div>

      <p className="text-sm text-slate-600 mb-1 flex items-center gap-1">
        <IconMapPin className="w-3.5 h-3.5 text-slate-400" />
        {camp?.district ? `${camp.district}, ` : ''}
        {camp?.state || ''}
      </p>
      <p className="text-sm text-slate-500 mb-3">
        {camp?.start_date ? formatDisplayDate(camp.start_date) : '—'}
        {' – '}
        {camp?.end_date ? formatDisplayDate(camp.end_date) : '—'}
      </p>

      <div className="flex items-center justify-between gap-3">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm flex-1">
          <div>
            <span className="text-slate-500">Patients: </span>
            <span className="font-medium text-slate-900">{num(camp?.patient_count)}</span>
          </div>
          <div>
            <span className="text-slate-500">Services: </span>
            <span className="font-medium text-slate-900">{num(camp?.service_count)}</span>
          </div>
          <div>
            <span className="text-slate-500">Done: </span>
            <span className="font-medium text-green-700">{num(camp?.completed_services)}</span>
          </div>
          <div>
            <span className="text-slate-500">Pending: </span>
            <span className="font-medium text-amber-700">{num(camp?.pending_services)}</span>
          </div>
        </div>
        <RadialProgress percentage={camp?.progress_percentage} size={48} stroke={5} />
      </div>
    </button>
  );
}

function LocationGroup({ location, onCampClick }) {
  const [open, setOpen] = useState(true);
  const camps = location?.camps || [];
  const campCount = num(location?.camp_count, camps.length);

  return (
    <div className="border border-slate-200 rounded-xl bg-slate-50/60">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="font-medium text-slate-800 flex items-center gap-2">
          <IconMapPin className="w-4 h-4 text-[#11a8a4]" />
          {location?.location || 'Unknown Location'}
        </span>
        <span className="text-sm text-slate-500 flex items-center gap-2">
          {campCount} camp{campCount === 1 ? '' : 's'}
          <IconChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {camps.length === 0 ? (
            <p className="text-sm text-slate-500 col-span-full">No camps at this location.</p>
          ) : (
            camps.map((camp) => (
              <CampCard key={camp.id} camp={camp} onClick={() => onCampClick(camp.id)} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ClientGroup({ client, onCampClick }) {
  const [open, setOpen] = useState(false);
  const locations = client?.locations || [];

  return (
    <div className="border border-slate-200 rounded-xl bg-white shadow-sm">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="font-semibold text-slate-900">{client?.client_name || 'Unknown Client'}</span>
        <span className="text-sm text-slate-500 flex items-center gap-2">
          {num(client?.camp_count)} camp{num(client?.camp_count) === 1 ? '' : 's'}
          <IconChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {locations.length === 0 ? (
            <p className="text-sm text-slate-500">No locations found for this client.</p>
          ) : (
            locations.map((loc, idx) => (
              <LocationGroup key={`${client.client_id}-${loc.location}-${idx}`} location={loc} onCampClick={onCampClick} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonBlock key={i} className="h-20" />
        ))}
      </div>
      <SkeletonBlock className="h-36 w-full" />
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonBlock key={i} className="h-16" />
        ))}
      </div>
    </div>
  );
}

export default function OperationsDashboard() {
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [monthCursor, setMonthCursor] = useState(new Date());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback((date, { isRefresh = false } = {}) => {
    const ymd = formatDateYMD(date);
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    getOperationsDashboard(ymd)
      .then((res) => setData(res))
      .catch((err) => {
        setData(null);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    fetchDashboard(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handlePrevDay = () => setSelectedDate((d) => addDays(d, -1));
  const handleNextDay = () => setSelectedDate((d) => addDays(d, 1));
  const handleToday = () => setSelectedDate(new Date());

  const handleDateInputChange = (e) => {
    if (!e.target.value) return;
    setSelectedDate(parseYMD(e.target.value));
  };

  const handleRefresh = () => fetchDashboard(selectedDate, { isRefresh: true });

  const handlePrevMonth = () => {
    const next = addMonths(monthCursor, -1);
    setMonthCursor(next);
    setSelectedDate(new Date(next.getFullYear(), next.getMonth(), 1));
  };
  const handleNextMonth = () => {
    const next = addMonths(monthCursor, 1);
    setMonthCursor(next);
    setSelectedDate(new Date(next.getFullYear(), next.getMonth(), 1));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userData');
    navigate('/login', { replace: true });
  };

  // Keep month cursor in sync when navigating by day across a month boundary.
  useEffect(() => {
    if (
      selectedDate.getFullYear() !== monthCursor.getFullYear() ||
      selectedDate.getMonth() !== monthCursor.getMonth()
    ) {
      setMonthCursor(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleCampClick = (campId) => {
    navigate(`/dashboard/operations/camp/${campId}`);
  };

  const errorMessage = error ? getOperationsErrorMessage(error) : null;
  const clients = data?.clients || [];
  const hasNoCamps = !loading && !error && clients.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#11a8a4] text-white flex items-center justify-center shrink-0">
            <IconStethoscope className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Operations Dashboard</h1>
            <p className="text-sm text-slate-500">Camps, patients and technician progress across all clients</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-2 bg-[#11a8a4] hover:bg-[#0b7d7a] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors"
          >
            <IconRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>

          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <IconLogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      {/* Date navigation */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevDay}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
          >
            &lt; Prev Day
          </button>
          <button
            onClick={handleToday}
            className="px-3 py-1.5 rounded-lg border border-[#99dfdb] bg-[#e6f7f6] text-[#0b7d7a] text-sm font-medium hover:bg-[#ccefed]"
          >
            Today
          </button>
          <button
            onClick={handleNextDay}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
          >
            Next Day &gt;
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">
            {formatDisplayDate(selectedDate)}
            {isSameDay(selectedDate, new Date()) && (
              <span className="ml-2 text-xs text-[#0b7d7a] font-semibold bg-[#e6f7f6] px-2 py-0.5 rounded-full">
                Today
              </span>
            )}
          </span>
          <input
            type="date"
            value={formatDateYMD(selectedDate)}
            onChange={handleDateInputChange}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#11a8a4]"
          />
        </div>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-center gap-4 text-sm">
        <button onClick={handlePrevMonth} className="text-slate-500 hover:text-[#0b7d7a] transition-colors">
          &lt; {formatMonthLabel(addMonths(monthCursor, -1))}
        </button>
        <span className="font-semibold text-slate-900">{formatMonthLabel(monthCursor)}</span>
        <button onClick={handleNextMonth} className="text-slate-500 hover:text-[#0b7d7a] transition-colors">
          {formatMonthLabel(addMonths(monthCursor, 1))} &gt;
        </button>
      </div>

      {/* Loading */}
      {loading && <DashboardSkeleton />}

      {/* Error */}
      {!loading && errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{errorMessage}</div>
      )}

      {/* Content */}
      {!loading && !errorMessage && data && (
        <>
          <SummaryCards summary={data.summary} />

          <div>
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Camp Status</h2>
            <CampStatusSummary campStatus={data.camp_status} />
          </div>

          <div>
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Clients</h2>

            {hasNoCamps ? (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
                No camps scheduled for this date.
              </div>
            ) : (
              <div className="space-y-3">
                {clients.map((client) => (
                  <ClientGroup key={client.client_id} client={client} onCampClick={handleCampClick} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}